/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpParams} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {NbWindowService} from '@nebular/theme';
import {FormlyFieldConfig, FormlyFormOptions} from '@ngx-formly/core';
import {TuiDayRange} from '@taiga-ui/cdk';
import {BreadcrumbDto, DamElasticDTO, DamEventService, FileService, PageApiResponse, StoreService} from 'compob2b-libs';
import {FileUploader} from 'ng2-file-upload';
import {EMPTY, from, Observable, Subscription} from 'rxjs';
import {catchError, concatMap, debounceTime, finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../../environments/translations.ru';
import {ListComponent} from '../../../../admin-shared/crud-components/list/list.component';
import {CrudConfig} from '../../../../admin-shared/interfaces/crud.config';
import {FormConfigService} from '../../../../admin-shared/services/form-config.service';
import {CatalogService} from '../../../pim/catalog/catalog.service';
import {DamFilterService} from '../../dam-filter.service';
import {DamService} from '../../dam.service';

interface DocumentFile {
  enabled: boolean;
  header: string;
  fileInput: File;
}

const COLUMNS = [
  {
    key: 'header',
    title: translations.header,
    placeholder: translations.header,
    searchEnabled: false,
    formly: true,
    grid: true,
    type: 'input',
    required: true,
  },
  {
    key: 'countVersion',
    title: translations.countVersion,
    placeholder: translations.countVersion,
    formly: true,
    type: 'input',
    grid: true
  },
  {
    key: 'statuses',
    // key: 'approved',
    // key: 'deleted',
    title: translations.status,
    placeholder: translations.status,
    formly: true,
    type: 'input',
    grid: true,
  },
  {
    key: 'managerName',
    title: translations.owner,
    placeholder: translations.owner,
    formly: true,
    type: 'input',
    width: '100%',
    grid: true
  },
  {
    key: 'tagsForPreview',
    title: translations.tags,
    placeholder: translations.tags,
    formly: true,
    type: 'input',
    grid: true
  },
  {
    key: 'enabled',
    // key: 'approved',
    // key: 'deleted',
    title: translations.status,
    placeholder: translations.status,
    formly: true,
    type: 'checkbox',
    grid: true,
  },
];

@Component({
  selector: 'app-dam-files-list',
  templateUrl: './dam-files-list.component.html',
  styleUrls: ['./dam-files-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DamFilesListComponent extends ListComponent<DamElasticDTO> implements OnInit {
  @ViewChild('actionTpl', {static: true}) actionTpl: TemplateRef<any>;
  @ViewChild('statusesTpl', {static: true}) statusesTpl: TemplateRef<any>;
  // private pageProductIDs: number[] = [];
  menuId: number;
  uploader: FileUploader;
  uploadURI: string;
  hasDropZoneOver: boolean;
  private subscription: Subscription;
  keyword = '';
  catalogBreadCrumbs: Observable<BreadcrumbDto[]>;
  productShow = false;
  selectedFiles: any[] = [];
  formFilter = new FormGroup({});
  formFilterFast = new FormGroup({});
  fieldsFeatures: FormlyFieldConfig[];
  fieldsFeaturesFast: FormlyFieldConfig[];
  options: FormlyFormOptions = {};
  optionsFast: FormlyFormOptions = {};
  modelFeatures: any = {};
  modelFeaturesFast: any = {};
  getFeatures = false;
  modelFeaturesArr: any = {};
  readonly showDamFilter$: Observable<boolean> = this.storeService.getShowDamFilter();
  readonly damUploadFinish$: Observable<boolean> = this.damEventService.getDamUploadFinish();
  showDamFilter: boolean;

  config: CrudConfig = {
    uri: '/dam/files',
    columns: COLUMNS,
    defaultSortColumn: 'header',
    defaultSortDirection: 'asc',
    title: translations.files,
    checkboxes: true
  };

  constructor(protected readonly formsConfig: FormConfigService,
              protected readonly windowService: NbWindowService,
              protected readonly fileService: FileService,
              protected readonly catalogService: CatalogService,
              protected readonly storeService: StoreService,
              protected readonly damService: DamService,
              protected readonly damFilter: DamFilterService,
              protected readonly damEventService: DamEventService
  ) {
    super(formsConfig, windowService, fileService);
  }

  ngOnInit(): void {
    this.showDamFilter$.pipe(tap((value) => {
      this.showDamFilter = value;
      this.cdr.detectChanges();
    }), takeUntil(this.unsubscribe)).subscribe();

    this.damUploadFinish$.pipe(tap(value => {
      if (value === true) {
        this.data = this.filterData().pipe(catchError(err => {
          this.handleError(err);
          return EMPTY;
        }));
      }
      this.cdr.detectChanges();
    }), takeUntil(this.unsubscribe)).subscribe();

    this.uploader = new FileUploader({
      url: null,
      disableMultipart: false,
      formatDataFunctionIsAsync: false,
      isHTML5: true,
      removeAfterUpload: false,
      autoUpload: false,
      maxFileSize: 25 * 1024 * 1024,
    });

    super.setConfig(this.config);
    this.configure();
    this.route.queryParamMap.pipe(
      tap(_ => {
        this.data = this.filterData().pipe(catchError(err => {
          this.handleError(err);
          return EMPTY;
        }));
      }),
      tap(params => {
        let catalogId = 1;
        if (Number(params.get('catalog_id')) > 1) {
          catalogId = Number(params.get('catalog_id'));
        }
        this.catalogBreadCrumbs = this.damService.getCatalogBreadCrumbs(catalogId);
      }),
      tap(params => this.keyword = params.get('keyword')),
      tap(params => this.menuId = Number(params.get('catalog_id'))),
      switchMap(params1 => {

        let catalogId = 1;
        if (Number(params1.get('catalog_id')) > 1) {
          catalogId = Number(params1.get('catalog_id'));
        }
        return this.damFilter.getProductFilter(this.config.uri, catalogId, params1);
      }),
      tap(_ => {
        this.loading = false;
        this.cdr.detectChanges();
      }),
      takeUntil(this.unsubscribe)
    ).subscribe(response => {
      this.fieldsFeatures = response.fieldsFeatures;
      this.fieldsFeatures.forEach(feature => {
        if (Array.isArray(feature.defaultValue)) {
          const defaultValue = [];
          feature.defaultValue.forEach(featureDefaultValue => {
            defaultValue.push(isNaN(featureDefaultValue)
              ? 'null'
              : featureDefaultValue);
          });
          feature.defaultValue = defaultValue;
        }
      });
      this.modelFeatures = {};
      this.getFeatures = true;
      this.cdr.detectChanges();
    });

    this.route.queryParamMap.pipe(
      debounceTime(500),
      switchMap(params1 => {

        let catalogId = 1;
        if (Number(params1.get('catalog_id')) > 1) {
          catalogId = Number(params1.get('catalog_id'));
        }
        return this.damFilter.getProductFilterFast(this.config.uri, catalogId, params1);
      }),
      tap(_ => this.cdr.detectChanges()),
      takeUntil(this.unsubscribe)
    ).subscribe(response => {
      this.fieldsFeaturesFast = response.fieldsFeatures;
      this.fieldsFeaturesFast.forEach(feature => {
        if (Array.isArray(feature.defaultValue)) {
          const defaultValue = [];
          feature.defaultValue.forEach(featureDefaultValue => {
            defaultValue.push(isNaN(featureDefaultValue)
              ? 'null'
              : featureDefaultValue);
          });
          feature.defaultValue = defaultValue;
        }
      });
      this.modelFeaturesFast = {};
      this.cdr.detectChanges();
    });

    this.formsConfig.setGridCellTemplate('statuses', this.statusesTpl);

  }

  filterFinish(id: number): void {
    window.location.reload();
  }

  protected filterData(): Observable<DamElasticDTO[]> {
    if (!this.loading) {
      this.loading = true;
      const params = this.route.snapshot.queryParams;

      this.updatePagerFromUri();
      const uri = this.getListURI(this.config.uri + '/' + (params.catalog_id || 0));

      return this.compoApiService.getWithParams<PageApiResponse<DamElasticDTO[]>>(uri, new HttpParams({fromObject: params}))
        .pipe(
          catchError(err => this.handleError(err)),
          tap(response => this.handleResponse(response.data)),
          map(response => response.data?.content)
        );
    }
  }

  public documentUri(fileName: string): string {
    return this.fileService.getPublicDocumentUri(fileName);
  }

  private uploadFile(uploadingFile: File): Observable<any> {
    this.uploadURI = '/dam/upload/' + this.menuId;
    return this.compoApiService.postForm<DocumentFile, string>(
      this.uploadURI, {enabled: true, header: uploadingFile.name, fileInput: uploadingFile}
    );
  }

  public download(fileName: string): void {
    this.fileService.downloadUrl('/documents/files/download/' + fileName)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        FileService.saveAs(response, fileName);
      });
  }

  public fileOver(e: any): void {
    this.hasDropZoneOver = e;
  }

  public upload(): void {
    this.loading = true;
    if (this.uploader.queue.length) {
      this.subscription = from(this.uploader.queue)
        .pipe(
          map(uploadingFile => uploadingFile._file),
          concatMap(uploadingFile => this.uploadFile(uploadingFile)),
          finalize(() => this.finishUpload()),
          takeUntil(this.unsubscribe)
        ).subscribe();
      this.hasDropZoneOver = false;
    }
  }

  private finishUpload(): void {
    this.uploader.clearQueue();
    this.subscription.unsubscribe();
    this.filterFinish(this.route.snapshot.params.catalog_id);
    this.loading = false;

  }

  searchByKeyword() {
    if (this.keyword === '') {
      this.keyword = null;
    }

    this.onNavigate({keyword: this.keyword, page: null});
  }

  openModalPopup() {
    // this.windowService.open(ModalDamComponent, {windowClass: 'popup-dam'});
  }

  showProductInfo() {
    this.productShow = true;
  }

  hideProductInfo() {
    this.productShow = false;
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    for (let i = 0; i < files.length; i++) {
      const fileExtension = files[i].name.split('.').pop()?.toLowerCase();
      if (fileExtension) {
        if (fileExtension === 'png' || fileExtension === 'jpg' || fileExtension === 'jpeg') {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            this.selectedFiles.push({
              name: files[i].name,
              type: 'image',
              data: e.target.result
            });
            this.cdr.detectChanges();
          };
          reader.readAsDataURL(files[i]);
        } else {
          this.selectedFiles.push({
            name: files[i].name,
            type: 'document',
            data: null
          });
          this.cdr.detectChanges();
        }
      }
    }
  }

  isImage(type: string): boolean {
    return type.startsWith('image');
  }

  public filterSubmit(): void {
    console.log(this.formFilter.valid);
    // if (this.formFilter.valid) {
    // this.damFilter.event({event: 'filter', value: null});
    this.onNavigate(this.convertModelFeatures());
  }

  public filterSubmitFast(): void {
    console.log(this.formFilterFast.valid);
    // if (this.formFilter.valid) {
    // this.damFilter.event({event: 'filter', value: null});
    this.onNavigate(this.convertModelFeatures());
    // }
  }

  reset(): void {
    this.showDamFilter = false;
    this.storeService.nextShowDamFilter(this.showDamFilter);

    super.reset();
  }

  private convertModelFeatures(): any {
    console.log(this.modelFeatures);
    console.log(this.modelFeaturesFast);
    console.log(this.modelFeaturesArr);
    for (const [key, valueItem] of Object.entries(this.modelFeatures)) {

      if (typeof valueItem === 'object') {
        if (valueItem instanceof TuiDayRange) {
          this.modelFeaturesArr[key + '_from'] = valueItem.from.getFormattedDay('YMD', '-');
          this.modelFeaturesArr[key + '_to'] = valueItem.to.getFormattedDay('YMD', '-');
        }
        console.log(Array.isArray(valueItem));
        if (Array.isArray(valueItem)) {
          const ret = [];
          for (const [key2, value2] of Object.entries(valueItem)) {
            if (value2) {
              ret.push(value2);
            }
          }

          if (ret.length > 0) {
            this.modelFeaturesArr[key] = ret.join(',');
          } else {
            delete this.modelFeaturesArr[key];
          }
        } else {

          this.modelFeaturesArr[key] = null;
          if (valueItem !== null && (valueItem['start'] !== undefined || valueItem['stop'] !== undefined)) {
            let urlValue = '';
            if (valueItem.hasOwnProperty('start') && valueItem['start'] !== null) {
              urlValue += valueItem['start'];
            }
            urlValue += ';';
            if (valueItem.hasOwnProperty('stop') && valueItem['stop'] !== null) {
              urlValue += valueItem['stop'];
            }
            if (urlValue !== ';') {
              this.modelFeaturesArr[key] = urlValue;
            }
          } else {
            delete this.modelFeaturesArr[key];
          }
          for (const key2 in valueItem) {
            if (valueItem.hasOwnProperty(key2) && key2 === 'value') {

              this.modelFeaturesArr[key] = valueItem[key2];
            }
          }

        }

      } else {
        if (valueItem !== undefined) {
          if ((this.damFilter.getYesNoFilterKeys().includes(key) && (valueItem === 'false' || valueItem === false))
            || valueItem !== 'false' && valueItem !== false && valueItem !== '' && valueItem !== '0' && valueItem !== 0) {
            this.modelFeaturesArr[key] = valueItem;
          } else {
            delete this.modelFeaturesArr[key];
          }
        }
      }
    }

    for (const [key, valueItem] of Object.entries(this.modelFeaturesFast)) {
      if (typeof valueItem === 'object') {
        if (Array.isArray(valueItem)) {
          const ret = [];
          for (const [key2, value2] of Object.entries(valueItem)) {
            if (value2) {
              ret.push(value2);
            }
          }
          if (ret.length > 0) {
            this.modelFeaturesArr[key] = ret.join(',');
          } else {
            delete this.modelFeaturesArr[key];
          }
        } else {

          this.modelFeaturesArr[key] = null;
          if (valueItem !== null && (valueItem['start'] !== undefined || valueItem['stop'] !== undefined)) {
            let urlValue = '';
            if (valueItem.hasOwnProperty('start') && valueItem['start'] !== null) {
              urlValue += valueItem['start'];
            }
            urlValue += ';';
            if (valueItem.hasOwnProperty('stop') && valueItem['stop'] !== null) {
              urlValue += valueItem['stop'];
            }
            if (urlValue !== ';') {
              this.modelFeaturesArr[key] = urlValue;
            }
          } else {
            delete this.modelFeaturesArr[key];
          }
          const ret = [];
          for (const key2 in valueItem) {

            if (valueItem.hasOwnProperty(key2) && key2 === 'value') {
              this.modelFeaturesArr[key] = valueItem[key2];
            } else {
              ret.push(key2);
            }
          }
          if (ret.length > 0) {
            this.modelFeaturesArr[key] = ret.join(',');
          } else {
            delete this.modelFeaturesArr[key];
          }

        }

      } else {
        if (valueItem !== undefined) {
          if ((this.damFilter.getYesNoFilterKeys().includes(key) && (valueItem === 'false' || valueItem === false))
            || valueItem !== 'false' && valueItem !== false && valueItem !== '' && valueItem !== '0' && valueItem !== 0) {
            this.modelFeaturesArr[key] = valueItem;
          } else {
            delete this.modelFeaturesArr[key];
          }
        }
      }
    }

    if (this.route.snapshot.queryParamMap.get('catalog_id') != null && this.route.snapshot.queryParamMap.get('catalog_id')
      !== '0' && this.route.snapshot.queryParamMap.get('catalog_id') !== '') {
      this.modelFeaturesArr.catalog_id = this.route.snapshot.queryParamMap.get('catalog_id');
    } else {
      delete this.modelFeaturesArr.catalog_id;
    }

    if (this.route.snapshot.queryParamMap.get('keyword') !== undefined && this.route.snapshot.queryParamMap.get('keyword')
      !== '' && this.route.snapshot.queryParamMap.get('keyword') !== null) {
      this.modelFeaturesArr.keyword = this.route.snapshot.queryParamMap.get('keyword');
    } else {
      delete this.modelFeaturesArr.keyword;
    }
    return this.modelFeaturesArr;
  }
}
